import type { ReactNode } from 'react';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { Link } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcRegistrationV1ProfileQueryServiceProfileGet } from '@interxfi/api-client/generated/hooks';
import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNode,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
  type ReadonlyListItemOpts,
} from '@interxfi/app-components/components/readonly-list/util';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { PlainIconButton } from '@interxfi/design-system/components/button/plain-icon/plain-icon-button';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import { TextLink } from '@interxfi/design-system/components/link';
import {
  defaultReadonlyListItemTitleTypographyProps,
  type ReadonlyListItem,
  type ReadonlyListItemRecord,
} from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { defaultValueStackedTypographyProps, defaultValueTypographyProps } from '@interxfi/design-system/components/list/readonly/item/value';
import type { DiffedReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/diffed-readonly-list-stack';
import { Pill } from '@interxfi/design-system/components/pill/pill';
import type { TypographyProps } from '@interxfi/design-system/components/typography';
import SvgIconInfo from '@interxfi/design-system/icons/react/icon-info';
import { buildTooltipInteraction } from '@interxfi/design-system/util/interaction';
import {
  IsoV1Country,
  type IxcRegistrationV1BeneficialOwner,
  type IxcRegistrationV1ControlPerson,
  type IxcRegistrationV1Employment,
  type IxcRegistrationV1Experience,
  type IxcRegistrationV1IndividualProfile,
  type IxcRegistrationV1IndividualW9,
  type IxcRegistrationV1InvestmentExperience,
  type IxcRegistrationV1InvestmentProfile,
  type IxcRegistrationV1OptionsExperience,
  type IxcRegistrationV1OrganizationProfile,
  type IxcRegistrationV1OrganizationW9,
  type IxcRegistrationV1ProfileKeys,
  type IxcRegistrationV1ProfileState,
  type IxcRegistrationV1ProfileType,
  type IxcRegistrationV1TrustedPerson,
} from '@interxfi/types/generated/api';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { isDeepEqual } from '@interxfi/utilities/equality/deep';
import { getBooleanFromUndefinedOrNullable } from '@interxfi/utilities/object/access';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import type { ReplaceValuesAndMakeOptional } from '@interxfi/utilities/types/replace';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getProgramDetailPath } from '../../userauth/program/program-routing';
import {
  getRegistrationAddressListItems,
  getRegistrationIndividualTaxIdentifierReadonlyListItem,
  getRegistrationNameListItems,
  getRegistrationOrganizationTaxIdentifierReadonlyListItem,
} from '../shared-registration-list-items';
import { formatPhone, normalizePhone } from '../shared-utils/phone';
import { IndividualProfileTableOfContentsItem } from './form/individual/individual-profile-form';
import { OrganizationProfileTableOfContentsItem } from './form/organization/organization-profile-form';
import { ProfileStatusPill } from './profile-status-pill';
import { getProfileEmploymentInformation, getProfileName } from './profile-utils';
import { getRegistrationProfileDetailPath } from './registration-profile-routing';
import {
  W9_BACKUP_WITHHOLDING_TOOLTIP,
  W9_CERTIFICATION_TOOLTIP,
  W9_EXEMPT_PAYEE_TOOLTIP,
  W9_FOREIGN_OWNERS_TOOLTIP,
  W9_SIGNED_MORE_THAN_A_YEAR_AGO_TOOLTIP,
} from './tooltips';
import { getRegistrationProfileVersionListItems } from './version/profile-version-list-items';

function ProfileListItem({ asLink, profileId }: { asLink: boolean; profileId: string }) {
  const { data, isPending } = useIxcRegistrationV1ProfileQueryServiceProfileGet(profileId ? { profileId } : undefined);
  const name = getProfileName(data?.profile);
  const linkPath = getRegistrationProfileDetailPath(profileId);
  const shortenedId = getShortenedId(profileId, 8);

  if (name && linkPath) {
    return asLink ? (
      <TextLink LinkElement={Link} size="small" to={linkPath}>
        {name}
      </TextLink>
    ) : (
      <span>{name}</span>
    );
  }

  if (isPending) {
    return null;
  }

  return asLink && linkPath ? (
    <TextLink LinkElement={Link} size="small" to={linkPath}>
      {shortenedId}
    </TextLink>
  ) : (
    <span>{shortenedId}</span>
  );
}

export function getReadonlyListItemProfile(
  id: string,
  label: ReactNode,
  profileId: string | undefined,
  opts?: ReadonlyListItemOpts & { asLink?: boolean },
): ReadonlyListItem {
  const { asLink = true, ...restOfOpts } = opts ?? {};

  return getReadonlyListItemNode(id, label, profileId ? <ProfileListItem asLink={Boolean(asLink)} profileId={profileId} /> : null, restOfOpts);
}

const pillLabelResetStyle = {
  ['--ixf-typography-transform' as string]: 'none',
  letterSpacing: 'normal',
  textTransform: 'none',
} as const;

export function getTrustedPersonListItems(data: IxcRegistrationV1TrustedPerson | undefined, prefix = '') {
  return {
    name: getRegistrationNameListItems(data?.name, `${prefix}.name`),
    email: getReadonlyListItemString(`${prefix}.email`, 'Email', data?.email),
    phone: getReadonlyListItemString(
      `${prefix}.phone`,
      (data?.phone?.length || 0) > 1 ? 'Phones' : 'Phone',
      data?.phone?.map(formatPhone).join('\n'),
    ),
    address: getRegistrationAddressListItems(data?.address, `${prefix}.address`),
    relationship: getReadonlyListItemString(`${prefix}.relationship`, 'Relationship', data?.relationship),
  } as const satisfies ReadonlyListItemRecord;
}

function formatInvestmentExperience(experience: IxcRegistrationV1Experience | undefined) {
  if (!experience) {
    return undefined;
  }

  const parts: string[] = [];

  if (experience.years !== undefined) {
    parts.push(experience.years === 1 ? '1 year' : `${experience.years} years`);
  }

  if (experience.transactionsPerYear !== undefined) {
    parts.push(experience.transactionsPerYear === 1 ? '1 yearly' : `${experience.transactionsPerYear} yearly`);
  }

  return parts.join(', ');
}

export function getInvestmentExperienceListItems(
  kind: keyof IxcRegistrationV1InvestmentExperience,
  data: IxcRegistrationV1Experience | undefined,
  prefix = '',
) {
  return {
    years: getReadonlyListItemNumber(`${prefix}.years`, 'Years of Experience', data?.years),
    transactionsPerYear: getReadonlyListItemNumber(`${prefix}.transactionsPerYear`, 'Transactions per Year', data?.transactionsPerYear),
    summary: getReadonlyListItemString(
      `${prefix}.summary`,
      match(kind)
        .with('margin', () => 'Margin')
        .with('equities', () => 'Equities')
        .with('options', () => 'Options')
        .with('mutualFunds', () => 'Mutual Funds')
        .with('bonds', () => 'Bonds')
        .with('futures', () => 'Futures')
        .exhaustive((s) => s as string),
      formatInvestmentExperience(data),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOptionsInvestmentExperienceListItems(data: IxcRegistrationV1OptionsExperience | undefined, prefix = '') {
  return {
    ...getInvestmentExperienceListItems('options', data as IxcRegistrationV1Experience, prefix),
    highestOptionStrategy: getReadonlyListItemTranslatedString(`${prefix}.highestOptionStrategy`, 'Highest Option Strategy', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1OptionsLevel',
      value: data?.highestOptionStrategy,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInvestmentProfileListItems(data: IxcRegistrationV1InvestmentProfile | undefined, prefix = '') {
  let taxRateAsPercentage: number | undefined;

  if (data?.usTaxRate !== undefined) {
    taxRateAsPercentage = data.usTaxRate / 100;
  }

  return {
    annualIncome: getReadonlyListItemCurrency(`${prefix}.annualIncome`, 'Annual Income', data?.annualIncome, { maximumFractionDigits: 0 }),
    netWorth: getReadonlyListItemCurrency(`${prefix}.netWorth`, 'Net Worth', data?.netWorth, { maximumFractionDigits: 0 }),
    liquidNetWorth: getReadonlyListItemCurrency(`${prefix}.liquidNetWorth`, 'Liquid Net Worth', data?.liquidNetWorth, { maximumFractionDigits: 0 }),
    usTaxRate: getReadonlyListItemNumber(`${prefix}.usTaxRate`, 'US Tax Rate', taxRateAsPercentage, { formatStyle: 'percent' }),
    investmentExperience: {
      margin: getInvestmentExperienceListItems('margin', data?.investmentExperience?.margin, `${prefix}.investmentExperience.margin`),
      equities: getInvestmentExperienceListItems('equities', data?.investmentExperience?.equities, `${prefix}.investmentExperience.equities`),
      mutualFunds: getInvestmentExperienceListItems(
        'mutualFunds',
        data?.investmentExperience?.mutualFunds,
        `${prefix}.investmentExperience.mutualFunds`,
      ),
      bonds: getInvestmentExperienceListItems('bonds', data?.investmentExperience?.bonds, `${prefix}.investmentExperience.bonds`),
      futures: getInvestmentExperienceListItems('futures', data?.investmentExperience?.futures, `${prefix}.investmentExperience.futures`),
      options: getOptionsInvestmentExperienceListItems(data?.investmentExperience?.options, `${prefix}.investmentExperience.options`),
    },
  } as const satisfies ReadonlyListItemRecord;
}

export function getIndividualProfileW9ListItems(data: IxcRegistrationV1IndividualW9 | undefined, prefix = '') {
  const w9SignedMoreThanAYearAgo = data?.signed && parseDate(data.signed).add({ years: 1 }).compare(today(getLocalTimeZone())) < 0;

  return {
    isProviding: getReadonlyListItemYesOrNo(`${prefix}isProviding`, 'Providing W-9', Boolean(data)),
    isSubjectToBackupWithholding: getReadonlyListItemYesOrNo(
      `${prefix}isSubjectToBackupWithholding`,
      'Subject to Backup Withholding',
      getBooleanFromUndefinedOrNullable(data, 'isSubjectToBackupWithholding'),
      {
        action: buildTooltipInteraction(
          W9_BACKUP_WITHHOLDING_TOOLTIP,
          <PlainIconButton aria-label="More information">
            <SvgIconInfo aria-hidden width={16} />
          </PlainIconButton>,
          { maxWidth: 200 },
        ),
      },
    ),
    certification: getReadonlyListItemYesOrNo(`${prefix}certification`, 'Certification', getBooleanFromUndefinedOrNullable(data, 'certification'), {
      action: buildTooltipInteraction(
        W9_CERTIFICATION_TOOLTIP,
        <PlainIconButton aria-label="More information">
          <SvgIconInfo aria-hidden width={16} />
        </PlainIconButton>,
        { maxWidth: 200 },
      ),
    }),
    signed: getReadonlyListItemDate(`${prefix}signed`, 'Signed', data?.signed, 'date_iso', {
      valueTypography:
        // Should appear as red if more than a year old
        w9SignedMoreThanAYearAgo ? { ...defaultValueTypographyProps, tone: 'danger-dk' } : defaultValueTypographyProps,
      action: w9SignedMoreThanAYearAgo
        ? buildTooltipInteraction(
            W9_SIGNED_MORE_THAN_A_YEAR_AGO_TOOLTIP,
            <PlainIconButton aria-label="More information">
              <SvgIconInfo aria-hidden width={16} />
            </PlainIconButton>,
            { maxWidth: 200 },
          )
        : undefined,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProfileEmploymentListItems(data: IxcRegistrationV1Employment | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1Employment',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('employed' in data) {
    return {
      ...typeNameColumn,
      employed: {
        '!type': 'employed',
        value: {
          institution: getReadonlyListItemString(`${prefix}institution`, 'Institution', data.employed.institution),
          occupation: getReadonlyListItemString(`${prefix}occupation`, 'Occupation', data.employed.occupation),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('selfEmployed' in data) {
    return {
      ...typeNameColumn,
      selfEmployed: {
        '!type': 'selfEmployed',
        value: {
          institution: getReadonlyListItemString(`${prefix}institution`, 'Institution', data.selfEmployed.institution),
          occupation: getReadonlyListItemString(`${prefix}occupation`, 'Occupation', data.selfEmployed.occupation),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('partTime' in data) {
    return {
      ...typeNameColumn,
      partTime: {
        '!type': 'partTime',
        value: {
          institution: getReadonlyListItemString(`${prefix}institution`, 'Institution', data.partTime.institution),
          occupation: getReadonlyListItemString(`${prefix}occupation`, 'Occupation', data.partTime.occupation),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('seasonal' in data) {
    return {
      ...typeNameColumn,
      seasonal: {
        '!type': 'seasonal',
        value: {
          institution: getReadonlyListItemString(`${prefix}institution`, 'Institution', data.seasonal.institution),
          occupation: getReadonlyListItemString(`${prefix}occupation`, 'Occupation', data.seasonal.occupation),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('unemployed' in data) {
    return {
      ...typeNameColumn,
      unemployed: {},
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('retired' in data) {
    return {
      ...typeNameColumn,
      retired: {},
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('student' in data) {
    return {
      ...typeNameColumn,
      student: {},
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('homemaker' in data) {
    return {
      ...typeNameColumn,
      homemaker: {},
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getIndividualProfileTypeListItems(data: IxcRegistrationV1IndividualProfile | undefined, prefix = '') {
  return {
    name: getRegistrationNameListItems(data?.name, `${prefix}name.`),
    email: getReadonlyListItemString(`${prefix}email.`, 'Email', data?.email),
    phone: getReadonlyListItemString(
      `${prefix}phone.`,
      (data?.phone?.length || 0) > 1 ? 'Phones' : 'Phone',
      data?.phone?.map(formatPhone).join('\n'),
    ),
    physicalAddress: getRegistrationAddressListItems(data?.physicalAddress, `${prefix}physicalAddress.`, 'Physical Address'),
    mailingAddress: getRegistrationAddressListItems(data?.mailingAddress, `${prefix}mailingAddress.`, 'Mailing Address'),
    birthDate: getReadonlyListItemDate(`${prefix}birthDate.`, 'Birth Date', data?.birthDate, 'date_iso'),
    countryOfCitizenship: getReadonlyListItemTranslatedString(`${prefix}countryOfCitizenship.`, 'Country of Citizenship', {
      namespace: 'isoV1',
      enumName: 'IsoV1Country',
      value: data?.countryOfCitizenship,
    }),
    taxIdentifier: getRegistrationIndividualTaxIdentifierReadonlyListItem(data?.taxIdentifier, `${prefix}.taxIdentifier`),
    maritalStatus: getReadonlyListItemTranslatedString(`${prefix}maritalStatus.`, 'Marital Status', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1MaritalStatus',
      value: data?.maritalStatus,
    }),
    employee: getReadonlyListItemYesOrNo(
      `${prefix}employee.`,
      'This individual or a member of their household is employed by the correspondent.',
      getBooleanFromUndefinedOrNullable(data, 'employee'),
      {
        titleTypography: {
          ...defaultReadonlyListItemTitleTypographyProps,
          wrap: 'break-word',
        },
      },
    ),
    employment: getProfileEmploymentListItems(data?.employment, `${prefix}employment.`),
    investmentProfile: getInvestmentProfileListItems(data?.investmentProfile, `${prefix}.investmentProfile`),
    affiliations: {
      corporateInsider: {
        companyName: getReadonlyListItemString(
          `${prefix}.affiliations.corporateInsider.companyName`,
          'Company Name',
          data?.affiliations?.corporateInsider?.companyName,
        ),
        symbol: getReadonlyListItemString(`${prefix}.affiliations.corporateInsider.symbol`, 'Symbol', data?.affiliations?.corporateInsider?.symbol),
      },
      politicallyExposedPerson: {
        country: getReadonlyListItemTranslatedString(`${prefix}.affiliations.politicallyExposedPerson.country`, 'Country', {
          namespace: 'isoV1',
          enumName: 'IsoV1Country',
          value: data?.affiliations?.politicallyExposedPerson?.country,
        }),
      },
      associatedPersons: (data?.affiliations?.associatedPersons || []).map((associatedPerson, index) => {
        const builtPrefix = `${prefix}.affiliations.associatedPersons${index}.`;

        return {
          name: getRegistrationNameListItems(associatedPerson.name, `${builtPrefix}name.`),
          employer: getReadonlyListItemString(`${builtPrefix}employer`, 'Employer', associatedPerson.employer),
          complianceOfficer: getReadonlyListItemString(`${builtPrefix}complianceOfficer`, 'Compliance Officer', associatedPerson.complianceOfficer),
          complianceOfficerEmail: getReadonlyListItemString(
            `${builtPrefix}complianceOfficerEmail`,
            'Compliance Officer Email',
            associatedPerson.complianceOfficerEmail,
          ),
          // To be used instead of the two fields above, situationally.
          complianceOfficerSummary: getReadonlyListItemString(
            `${builtPrefix}.complianceOfficerSummary`,
            'Compliance Officer',
            [associatedPerson.complianceOfficer, associatedPerson.complianceOfficerEmail].filter(Boolean).join(', '),
          ),
        };
      }),
    },
    trustedContactPersons: (data?.trustedPersons || []).map((trustedPerson, index) =>
      getTrustedPersonListItems(trustedPerson, `${prefix}trustedContactPersons.${index}.`),
    ),
    w9: getIndividualProfileW9ListItems(data?.w9, `${prefix}w9.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationBeneficialOwnerListItems(data: IxcRegistrationV1BeneficialOwner | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1BeneficialOwner',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('direct' in data) {
    return {
      ...typeNameColumn,
      direct: {
        '!type': 'direct',
        value: {
          profileId: getReadonlyListItemId(
            `${prefix}direct.profileId`,
            'Profile ID',
            data.direct.profileId,
            getRegistrationProfileDetailPath(data.direct.profileId),
          ),
          profile: getReadonlyListItemProfile(`${prefix}direct.profile`, 'Profile', data.direct.profileId),
          ownership: getReadonlyListItemNumber(`${prefix}direct.ownership`, 'Ownership Percentage', data.direct.ownership, {
            formatStyle: 'percent',
          }),
          signer: getReadonlyListItemYesOrNo(`${prefix}direct.signer`, 'Signer', getBooleanFromUndefinedOrNullable(data.direct, 'signer')),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('indirect' in data) {
    return {
      ...typeNameColumn,
      indirect: {
        '!type': 'indirect',
        value: {
          profileId: getReadonlyListItemId(
            `${prefix}indirect.profileId`,
            'Profile ID',
            data.indirect.profileId,
            getRegistrationProfileDetailPath(data.indirect.profileId),
          ),
          profile: getReadonlyListItemProfile(`${prefix}indirect.profile`, 'Profile', data.indirect.profileId),
          ownership: getReadonlyListItemNumber(`${prefix}indirect.ownership`, 'Ownership Percentage', data.indirect.ownership, {
            formatStyle: 'percent',
          }),
          signer: getReadonlyListItemYesOrNo(`${prefix}indirect.signer`, 'Signer', getBooleanFromUndefinedOrNullable(data.indirect, 'signer')),
          beneficialOwnerEntity: getReadonlyListItemString(
            `${prefix}indirect.beneficialOwnerEntity`,
            'Beneficial Owner Entity',
            data.indirect.beneficialOwnerEntity,
          ),
          passThroughEntities: getReadonlyListItemString(
            `${prefix}indirect.passThroughEntities`,
            'Pass-Through Entities',
            data.indirect.passThroughEntities,
          ),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getRegistrationControlPersonListItems(data: IxcRegistrationV1ControlPerson | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1ControlPerson',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('beneficialOwner' in data) {
    return {
      ...typeNameColumn,
      beneficialOwner: {
        '!type': 'beneficialOwner',
        value: getRegistrationBeneficialOwnerListItems(data.beneficialOwner, `${prefix}beneficialOwner.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('officer' in data) {
    return {
      ...typeNameColumn,
      officer: {
        '!type': 'officer',
        value: {
          profileId: getReadonlyListItemId(
            `${prefix}officer.profileId`,
            'Profile ID',
            data.officer.profileId,
            getRegistrationProfileDetailPath(data.officer.profileId),
          ),
          profile: getReadonlyListItemProfile(`${prefix}officer.profile`, 'Profile', data.officer.profileId),
          title: getReadonlyListItemString(`${prefix}officer.title`, 'Title', data.officer.title),
          signer: getReadonlyListItemYesOrNo(`${prefix}officer.signer`, 'Signer', getBooleanFromUndefinedOrNullable(data.officer, 'signer')),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('director' in data) {
    return {
      ...typeNameColumn,
      director: {
        '!type': 'director',
        value: {
          profileId: getReadonlyListItemId(
            `${prefix}director.profileId`,
            'Profile ID',
            data.director.profileId,
            getRegistrationProfileDetailPath(data.director.profileId),
          ),
          profile: getReadonlyListItemProfile(`${prefix}director.profile`, 'Profile', data.director.profileId),
          title: getReadonlyListItemString(`${prefix}director.title`, 'Title', data.director.title),
          signer: getReadonlyListItemYesOrNo(`${prefix}director.signer`, 'Signer', getBooleanFromUndefinedOrNullable(data.director, 'signer')),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('other' in data) {
    return {
      ...typeNameColumn,
      other: {
        '!type': 'other',
        value: {
          profileId: getReadonlyListItemId(
            `${prefix}other.profileId`,
            'Profile ID',
            data.other.profileId,
            getRegistrationProfileDetailPath(data.other.profileId),
          ),
          profile: getReadonlyListItemProfile(`${prefix}other.profile`, 'Profile', data.other.profileId),
          title: getReadonlyListItemString(`${prefix}other.title`, 'Title', data.other.title),
          signer: getReadonlyListItemYesOrNo(`${prefix}other.signer`, 'Signer', getBooleanFromUndefinedOrNullable(data.other, 'signer')),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getOrganizationProfileW9ListItems(data: IxcRegistrationV1OrganizationW9 | undefined, prefix = '') {
  const w9SignedMoreThanAYearAgo = data?.signed && parseDate(data.signed).add({ years: 1 }).compare(today(getLocalTimeZone())) < 0;

  return {
    isSubjectToBackupWithholding: getReadonlyListItemYesOrNo(
      `${prefix}isSubjectToBackupWithholding`,
      'Subject to Backup Withholding',
      getBooleanFromUndefinedOrNullable(data, 'isSubjectToBackupWithholding'),
      {
        action: buildTooltipInteraction(
          W9_BACKUP_WITHHOLDING_TOOLTIP,
          <PlainIconButton aria-label="More information">
            <SvgIconInfo aria-hidden width={16} />
          </PlainIconButton>,
          { maxWidth: 200 },
        ),
      },
    ),
    certification: getReadonlyListItemYesOrNo(`${prefix}certification`, 'Certification', getBooleanFromUndefinedOrNullable(data, 'certification'), {
      action: buildTooltipInteraction(
        W9_CERTIFICATION_TOOLTIP,
        <PlainIconButton aria-label="More information">
          <SvgIconInfo aria-hidden width={16} />
        </PlainIconButton>,
        { maxWidth: 200 },
      ),
    }),
    foreignOwners: getReadonlyListItemYesOrNo(`${prefix}foreignOwners`, 'Foreign Owners', getBooleanFromUndefinedOrNullable(data, 'foreignOwners'), {
      action: buildTooltipInteraction(
        W9_FOREIGN_OWNERS_TOOLTIP,
        <PlainIconButton aria-label="More information">
          <SvgIconInfo aria-hidden width={16} />
        </PlainIconButton>,
        { maxWidth: 200 },
      ),
    }),
    exemptPayeeCode: getReadonlyListItemTranslatedString(
      `${prefix}exemptPayeeCode`,
      'Exempt Payee Code',
      {
        namespace: 'ixcRegistrationV1',
        enumName: 'IxcRegistrationV1ExemptPayeeCode',
        value: data?.exemptPayeeCode,
      },
      {
        action: buildTooltipInteraction(
          W9_EXEMPT_PAYEE_TOOLTIP,
          <PlainIconButton aria-label="More information">
            <SvgIconInfo aria-hidden width={16} />
          </PlainIconButton>,
          { maxWidth: 200 },
        ),
      },
    ),
    signed: getReadonlyListItemDate(`${prefix}signed`, 'Signed', data?.signed, 'date_iso', {
      valueTypography:
        // Should appear as red if more than a year old
        w9SignedMoreThanAYearAgo ? { ...defaultValueTypographyProps, tone: 'danger-dk' } : defaultValueTypographyProps,
      action: w9SignedMoreThanAYearAgo
        ? buildTooltipInteraction(
            W9_SIGNED_MORE_THAN_A_YEAR_AGO_TOOLTIP,
            <PlainIconButton aria-label="More information">
              <SvgIconInfo aria-hidden width={16} />
            </PlainIconButton>,
            { maxWidth: 200 },
          )
        : undefined,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrganizationProfileTypeListItems(data: IxcRegistrationV1OrganizationProfile | undefined, prefix = '') {
  return {
    legalName: getReadonlyListItemString(`${prefix}.legalName`, 'Legal Name', data?.legalName),
    dbaName: getReadonlyListItemString(`${prefix}.dbaName`, 'DBA', data?.dbaName),
    // Use either the legalName & dbaName or mergedName, not both
    mergedName: getReadonlyListItemString(
      `${prefix}.mergedName`,
      'Name',
      [data?.legalName, data?.dbaName ? `(DBA ${data.dbaName})` : undefined].filter(Boolean).join(' '),
    ),
    entityType: getReadonlyListItemTranslatedString(`${prefix}.entityType`, 'Entity Type', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1EntityType',
      value: data?.entityType,
    }),
    countryOfFormation: getReadonlyListItemTranslatedString(`${prefix}.countryOfFormation`, 'Country of Formation', {
      namespace: 'isoV1',
      enumName: 'IsoV1Country',
      value: data?.countryOfFormation,
    }),
    stateOfFormation:
      data?.stateOfFormation || data?.countryOfFormation === IsoV1Country.Us
        ? getReadonlyListItemTranslatedString(`${prefix}.stateOfFormation`, 'State of Formation', {
            namespace: 'ixcRegistrationV1',
            enumName: 'IxcRegistrationV1UsState',
            value: data.stateOfFormation,
          })
        : undefined,
    dateOfFormation: getReadonlyListItemDate(`${prefix}.dateOfFormation`, 'Date of Formation', data?.dateOfFormation, 'date_iso'),
    natureOfBusiness: getReadonlyListItemString(`${prefix}.natureOfBusiness`, 'Nature of Business', data?.natureOfBusiness),
    businessClassification: getReadonlyListItemTranslatedString(`${prefix}.businessClassification`, 'Business Classification', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1BusinessClassification',
      value: data?.businessClassification,
    }),
    taxIdentifier: getRegistrationOrganizationTaxIdentifierReadonlyListItem(data?.taxIdentifier, `${prefix}.taxIdentifier`),
    companyAddress: getRegistrationAddressListItems(data?.companyAddress, `${prefix}companyAddress.`, 'Company Address'),
    mailingAddress: getRegistrationAddressListItems(data?.mailingAddress, `${prefix}mailingAddress.`, 'Mailing Address'),
    email: getReadonlyListItemString(`${prefix}.email`, 'Email', data?.email),
    phone: getReadonlyListItemString(
      `${prefix}.phone`,
      (data?.phone?.length || 0) > 1 ? 'Phones' : 'Phone',
      data?.phone?.map(formatPhone).join('\n'),
    ),
    controlPersons: (data?.controlPersons || []).map((controlPerson, index) =>
      getRegistrationControlPersonListItems(controlPerson, `${prefix}controlPersons.${index}.`),
    ),
    w9: getOrganizationProfileW9ListItems(data?.w9, `${prefix}w9.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileTypeListItems(data: IxcRegistrationV1ProfileType | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString('typeName', 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1ProfileType',
      value: data?.['!type'],
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('organization' in data) {
    return {
      ...typeNameColumn,
      organization: {
        '!type': 'organization',
        value: getOrganizationProfileTypeListItems(data.organization, prefix),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('individual' in data) {
    return {
      ...typeNameColumn,
      individual: {
        '!type': 'individual',
        value: getIndividualProfileTypeListItems(data.individual, prefix),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

function getIndividualMainGroupItems(
  items: ReturnType<typeof getIndividualProfileTypeListItems>,
  existingItems: ReturnType<typeof getIndividualProfileTypeListItems>,
  newData: IxcRegistrationV1IndividualProfile | undefined,
  existingData: IxcRegistrationV1IndividualProfile | undefined,
) {
  const mainItemSet = existingData ? existingItems : items;

  return {
    items: [
      mainItemSet.name.formatted,
      mainItemSet.maritalStatus,
      mainItemSet.taxIdentifier,
      mainItemSet.physicalAddress.formatted,
      mainItemSet.mailingAddress.formatted,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newData
        ? ([
            ...(isDeepEqual(existingData.name, newData.name) ? [] : [items.name.formatted]),
            ...(isDeepEqual(existingData.maritalStatus, newData.maritalStatus) ? [] : [items.maritalStatus]),
            ...(isDeepEqual(existingData.taxIdentifier, newData.taxIdentifier) ? [] : [items.taxIdentifier]),
            ...(isDeepEqual(existingData.physicalAddress, newData.physicalAddress) ? [] : [items.physicalAddress.formatted]),
            ...(isDeepEqual(existingData.mailingAddress, newData.mailingAddress) ? [] : [items.mailingAddress.formatted]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

function getIndividualSecondaryGroupItems(
  items: ReturnType<typeof getIndividualProfileTypeListItems>,
  existingItems: ReturnType<typeof getIndividualProfileTypeListItems>,
  newData: IxcRegistrationV1IndividualProfile | undefined,
  existingData: IxcRegistrationV1IndividualProfile | undefined,
) {
  const mainItemSet = existingData ? existingItems : items;

  return {
    items: [
      mainItemSet.email,
      mainItemSet.countryOfCitizenship,
      mainItemSet.phone,
      mainItemSet.birthDate,
      { ...mainItemSet.employment.typeName, title: 'Employment Type' },
      ...match(mainItemSet.employment)
        .with({ employed: P.not(P.nullish) }, (e) => [e.employed.value.occupation, e.employed.value.institution])
        .with({ selfEmployed: P.not(P.nullish) }, (e) => [e.selfEmployed.value.occupation, e.selfEmployed.value.institution])
        .with({ seasonal: P.not(P.nullish) }, (e) => [e.seasonal.value.occupation, e.seasonal.value.institution])
        .with({ partTime: P.not(P.nullish) }, (e) => [e.partTime.value.occupation, e.partTime.value.institution])
        .otherwise(() => []),
      mainItemSet.employee,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newData
        ? ([
            ...(isDeepEqual(existingData.email, newData.email) ? [] : [items.email]),
            ...(isDeepEqual(existingData.countryOfCitizenship, newData.countryOfCitizenship) ? [] : [items.countryOfCitizenship]),
            ...(isDeepEqual(existingData.phone?.map(normalizePhone), newData.phone?.map(normalizePhone)) ? [] : [items.phone]),
            ...(isDeepEqual(existingData.birthDate, newData.birthDate) ? [] : [items.birthDate]),
            ...(isDeepEqual(getOneOfType(existingData.employment), getOneOfType(newData.employment))
              ? []
              : [{ ...items.employment.typeName, title: 'Employment Type' }]),
            ...(isDeepEqual(
              getProfileEmploymentInformation(existingData.employment, 'occupation'),
              getProfileEmploymentInformation(newData.employment, 'occupation'),
            )
              ? []
              : match(items.employment)
                  .with({ employed: P.not(P.nullish) }, (e) => [e.employed.value.occupation])
                  .with({ selfEmployed: P.not(P.nullish) }, (e) => [e.selfEmployed.value.occupation])
                  .with({ seasonal: P.not(P.nullish) }, (e) => [e.seasonal.value.occupation])
                  .with({ partTime: P.not(P.nullish) }, (e) => [e.partTime.value.occupation])
                  .otherwise(() => [])),
            ...(isDeepEqual(
              getProfileEmploymentInformation(existingData.employment, 'institution'),
              getProfileEmploymentInformation(newData.employment, 'institution'),
            )
              ? []
              : match(items.employment)
                  .with({ employed: P.not(P.nullish) }, (e) => [e.employed.value.institution])
                  .with({ selfEmployed: P.not(P.nullish) }, (e) => [e.selfEmployed.value.institution])
                  .with({ seasonal: P.not(P.nullish) }, (e) => [e.seasonal.value.institution])
                  .with({ partTime: P.not(P.nullish) }, (e) => [e.partTime.value.institution])
                  .otherwise(() => [])),
            ...(isDeepEqual(Boolean(existingData.employee), Boolean(newData.employee)) ? [] : [items.employee]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

function getInvestmentProfileGroupItems(
  items: ReturnType<typeof getIndividualProfileTypeListItems>,
  existingItems: ReturnType<typeof getIndividualProfileTypeListItems>,
  newData: IxcRegistrationV1IndividualProfile | undefined,
  existingData: IxcRegistrationV1IndividualProfile | undefined,
) {
  const mainItemSet = existingData ? existingItems : items;

  return {
    items: [
      mainItemSet.investmentProfile.annualIncome,
      mainItemSet.investmentProfile.liquidNetWorth,
      mainItemSet.investmentProfile.netWorth,
      mainItemSet.investmentProfile.usTaxRate,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newData
        ? ([
            ...(isDeepEqual(existingData.investmentProfile?.annualIncome, newData.investmentProfile?.annualIncome)
              ? []
              : [items.investmentProfile.annualIncome]),
            ...(isDeepEqual(existingData.investmentProfile?.liquidNetWorth, newData.investmentProfile?.liquidNetWorth)
              ? []
              : [items.investmentProfile.liquidNetWorth]),
            ...(isDeepEqual(existingData.investmentProfile?.netWorth, newData.investmentProfile?.netWorth) ? [] : [items.investmentProfile.netWorth]),
            ...(isDeepEqual(existingData.investmentProfile?.usTaxRate, newData.investmentProfile?.usTaxRate)
              ? []
              : [items.investmentProfile.usTaxRate]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

function getInvestmentExperienceGroupItems(
  items: ReturnType<typeof getIndividualProfileTypeListItems>,
  existingItems: ReturnType<typeof getIndividualProfileTypeListItems>,
  newData: IxcRegistrationV1IndividualProfile | undefined,
  existingData: IxcRegistrationV1IndividualProfile | undefined,
) {
  const mainItemSet = existingData ? existingItems : items;

  return {
    items: [
      mainItemSet.investmentProfile.investmentExperience.equities.summary,
      mainItemSet.investmentProfile.investmentExperience.options.summary,
      mainItemSet.investmentProfile.investmentExperience.mutualFunds.summary,
      mainItemSet.investmentProfile.investmentExperience.bonds.summary,
      mainItemSet.investmentProfile.investmentExperience.futures.summary,
      mainItemSet.investmentProfile.investmentExperience.margin.summary,
      mainItemSet.investmentProfile.investmentExperience.options.highestOptionStrategy,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newData
        ? ([
            ...(isDeepEqual(existingData.investmentProfile?.investmentExperience?.equities, newData.investmentProfile?.investmentExperience?.equities)
              ? []
              : [items.investmentProfile.investmentExperience.equities.summary]),
            // This list item is a subset of the "options" item, so we only show it if any of the items in that subset have changed
            ...(isDeepEqual(
              existingData?.investmentProfile?.investmentExperience?.options
                ? {
                    transactionsPerYear: existingData.investmentProfile.investmentExperience.options.transactionsPerYear,
                    years: existingData.investmentProfile.investmentExperience.options.years,
                  }
                : undefined,
              newData?.investmentProfile?.investmentExperience?.options
                ? {
                    transactionsPerYear: newData.investmentProfile.investmentExperience.options.transactionsPerYear,
                    years: newData.investmentProfile.investmentExperience.options.years,
                  }
                : undefined,
            )
              ? []
              : [items.investmentProfile.investmentExperience.options.summary]),
            ...(isDeepEqual(
              existingData.investmentProfile?.investmentExperience?.mutualFunds,
              newData.investmentProfile?.investmentExperience?.mutualFunds,
            )
              ? []
              : [items.investmentProfile.investmentExperience.mutualFunds.summary]),
            ...(isDeepEqual(existingData.investmentProfile?.investmentExperience?.bonds, newData.investmentProfile?.investmentExperience?.bonds)
              ? []
              : [items.investmentProfile.investmentExperience.bonds.summary]),
            ...(isDeepEqual(existingData.investmentProfile?.investmentExperience?.futures, newData.investmentProfile?.investmentExperience?.futures)
              ? []
              : [items.investmentProfile.investmentExperience.futures.summary]),
            ...(isDeepEqual(existingData.investmentProfile?.investmentExperience?.margin, newData.investmentProfile?.investmentExperience?.margin)
              ? []
              : [items.investmentProfile.investmentExperience.margin.summary]),
            ...(isDeepEqual(
              existingData.investmentProfile?.investmentExperience?.options?.highestOptionStrategy,
              newData.investmentProfile?.investmentExperience?.options?.highestOptionStrategy,
            )
              ? []
              : [items.investmentProfile.investmentExperience.options.highestOptionStrategy]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

function getIndividualW9GroupItems(
  items: ReturnType<typeof getIndividualProfileTypeListItems>,
  existingItems: ReturnType<typeof getIndividualProfileTypeListItems>,
  newData: IxcRegistrationV1IndividualProfile | undefined,
  existingData: IxcRegistrationV1IndividualProfile | undefined,
) {
  const mainItemSet = existingData ? existingItems : items;

  return {
    items: [
      mainItemSet.w9.isProviding,
      mainItemSet.w9.isSubjectToBackupWithholding,
      mainItemSet.w9.certification,
      mainItemSet.w9.signed,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newData
        ? ([
            ...(isDeepEqual(Boolean(existingData.w9), Boolean(newData.w9)) ? [] : [items.w9.isProviding]),
            ...(isDeepEqual(Boolean(existingData.w9?.isSubjectToBackupWithholding), Boolean(newData.w9?.isSubjectToBackupWithholding))
              ? []
              : [items.w9.isSubjectToBackupWithholding]),
            ...(isDeepEqual(Boolean(existingData.w9?.certification), Boolean(newData.w9?.certification)) ? [] : [items.w9.certification]),
            ...(isDeepEqual(existingData.w9?.signed, newData.w9?.signed) ? [] : [items.w9.signed]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

function getTrustedContactPersonGroupItems(
  personIndex: number,
  items: ReturnType<typeof getIndividualProfileTypeListItems>,
  existingItems: ReturnType<typeof getIndividualProfileTypeListItems>,
  newData: IxcRegistrationV1IndividualProfile | undefined,
  existingData: IxcRegistrationV1IndividualProfile | undefined,
) {
  const newItems = items.trustedContactPersons[personIndex];
  const mainItemSet = existingData ? existingItems.trustedContactPersons[personIndex] : newItems;

  const newPersonData = newData?.trustedPersons?.[personIndex];
  const existingPersonData = existingData?.trustedPersons?.[personIndex];

  const titleText = 'Trusted Contact Person';

  // Handle the case when a trusted contact person is removed
  if (mainItemSet && !newItems) {
    return {
      title: (
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          <span style={pillLabelResetStyle}>
            <Pill size="medium" variant="danger">
              Removed
            </Pill>
          </span>

          <span>{titleText}</span>
        </HorizontalStack>
      ),
      items: [mainItemSet.name.formatted, mainItemSet.email, mainItemSet.address.formatted, mainItemSet.relationship, mainItemSet.phone].map(
        (item) =>
          ({
            ...item,
            valueTypography: {
              ...(item.columns === 1 ? defaultValueStackedTypographyProps : defaultValueTypographyProps),
              ...item.valueTypography,
              decoration: 'strikethrough',
            } as TypographyProps,
          }) as const,
      ),
      changeItems: undefined,
    } as const;
  }

  // Handle the case when a new trusted contact person is added
  if (!mainItemSet && newItems) {
    return {
      title: (
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          <span style={pillLabelResetStyle}>
            <Pill size="medium" variant="tertiary">
              Addition
            </Pill>
          </span>

          <span>{titleText}</span>
        </HorizontalStack>
      ),
      items: [
        newItems.name.formatted,
        newItems.email,
        newItems.address.formatted,
        newItems.relationship,
        newItems.phone,
      ] as const satisfies ReadonlyListItem[],
      changeItems: undefined,
    } as const;
  }

  return {
    title: titleText,
    items: [
      mainItemSet.name.formatted,
      mainItemSet.email,
      mainItemSet.address.formatted,
      mainItemSet.relationship,
      mainItemSet.phone,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newItems
        ? ([
            ...(isDeepEqual(existingPersonData?.name, newPersonData?.name) ? [] : [newItems.name.formatted]),
            ...(isDeepEqual(existingPersonData?.email, newPersonData?.email) ? [] : [newItems.email]),
            ...(isDeepEqual(existingPersonData?.address, newPersonData?.address) ? [] : [newItems.address.formatted]),
            ...(isDeepEqual(existingPersonData?.relationship, newPersonData?.relationship) ? [] : [newItems.relationship]),
            ...(isDeepEqual(existingPersonData?.phone?.map(normalizePhone), newPersonData?.phone?.map(normalizePhone)) ? [] : [newItems.phone]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

function getAssociatedPersonGroupItems(
  personIndex: number,
  items: ReturnType<typeof getIndividualProfileTypeListItems>,
  existingItems: ReturnType<typeof getIndividualProfileTypeListItems>,
  newData: IxcRegistrationV1IndividualProfile | undefined,
  existingData: IxcRegistrationV1IndividualProfile | undefined,
) {
  const newItems = items.affiliations.associatedPersons[personIndex];
  const mainItemSet = existingData ? existingItems.affiliations.associatedPersons[personIndex] : newItems;

  const newPersonData = newData?.affiliations?.associatedPersons?.[personIndex];
  const existingPersonData = existingData?.affiliations?.associatedPersons?.[personIndex];

  const newIsProfileOwner = isDeepEqual(newPersonData?.name, newData?.name);
  const existingIsProfileOwner = isDeepEqual(existingPersonData?.name, existingData?.name);

  const mainItemSetProfileOwner = existingData ? existingIsProfileOwner : newIsProfileOwner;

  const titleText = (existingData ? existingIsProfileOwner : newIsProfileOwner)
    ? 'Associated Person'
    : 'Associated Person (Household Member or Independent)';

  // Handle the case when an associated person is removed
  if (mainItemSet && !newItems) {
    return {
      title: (
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          <span style={pillLabelResetStyle}>
            <Pill size="medium" variant="danger">
              Removed
            </Pill>
          </span>

          <span>{titleText}</span>
        </HorizontalStack>
      ),
      items: [...(mainItemSetProfileOwner ? [] : [mainItemSet.name.formatted]), mainItemSet.employer, mainItemSet.complianceOfficerSummary].map(
        (item) =>
          ({
            ...item,
            valueTypography: {
              ...(item.columns === 1 ? defaultValueStackedTypographyProps : defaultValueTypographyProps),
              ...item.valueTypography,
              decoration: 'strikethrough',
            } as TypographyProps,
          }) as const,
      ),
      changeItems: undefined,
    } as const;
  }

  // Handle the case when a new associated person is added
  if (!mainItemSet && newItems) {
    return {
      title: (
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          <span style={pillLabelResetStyle}>
            <Pill size="medium" variant="tertiary">
              Addition
            </Pill>
          </span>

          <span>{titleText}</span>
        </HorizontalStack>
      ),
      items: [
        ...(newIsProfileOwner ? [] : [newItems.name.formatted]),
        newItems.employer,
        newItems.complianceOfficerSummary,
      ] as const satisfies ReadonlyListItem[],
      changeItems: undefined,
    } as const;
  }

  return {
    title: titleText,
    items: [
      ...(mainItemSetProfileOwner ? [] : [mainItemSet.name.formatted]),
      mainItemSet.employer,
      mainItemSet.complianceOfficerSummary,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newItems
        ? ([
            ...(!newIsProfileOwner || isDeepEqual(existingPersonData?.name, newPersonData?.name) ? [] : [newItems.name.formatted]),
            ...(isDeepEqual(existingPersonData?.employer, newPersonData?.employer) ? [] : [newItems.employer]),
            ...(isDeepEqual(existingPersonData?.complianceOfficer, newPersonData?.complianceOfficer) ? [] : [newItems.complianceOfficer]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

function getPoliticallyExposedPersonGroupItems(
  items: ReturnType<typeof getIndividualProfileTypeListItems>,
  existingItems: ReturnType<typeof getIndividualProfileTypeListItems>,
  newData: IxcRegistrationV1IndividualProfile | undefined,
  existingData: IxcRegistrationV1IndividualProfile | undefined,
) {
  const mainItemSet = existingData ? existingItems : items;

  return {
    items: [mainItemSet.affiliations.politicallyExposedPerson.country] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newData
        ? ([
            ...(isDeepEqual(existingData.affiliations?.politicallyExposedPerson?.country, newData.affiliations?.politicallyExposedPerson?.country)
              ? []
              : [items.affiliations.politicallyExposedPerson.country]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

function getCorporateInsiderGroupItems(
  items: ReturnType<typeof getIndividualProfileTypeListItems>,
  existingItems: ReturnType<typeof getIndividualProfileTypeListItems>,
  newData: IxcRegistrationV1IndividualProfile | undefined,
  existingData: IxcRegistrationV1IndividualProfile | undefined,
) {
  const mainItemSet = existingData ? existingItems : items;

  return {
    items: [
      mainItemSet.affiliations.corporateInsider.companyName,
      mainItemSet.affiliations.corporateInsider.symbol,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newData
        ? ([
            ...(isDeepEqual(existingData.affiliations?.corporateInsider?.companyName, newData.affiliations?.corporateInsider?.companyName)
              ? []
              : [items.affiliations.corporateInsider.companyName]),
            ...(isDeepEqual(existingData.affiliations?.corporateInsider?.symbol, newData.affiliations?.corporateInsider?.symbol)
              ? []
              : [items.affiliations.corporateInsider.symbol]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

export function getRegistrationIndividualProfileListItemSummary(
  profile: IxcRegistrationV1IndividualProfile | undefined,
  diffingProfile?: IxcRegistrationV1IndividualProfile,
) {
  const listItems = getIndividualProfileTypeListItems(profile);
  const existingListItems = getIndividualProfileTypeListItems(diffingProfile);

  const groups: DiffedReadonlyListGroup[] = [
    {
      id: IndividualProfileTableOfContentsItem.PersonalInformation,
      ...getIndividualMainGroupItems(listItems, existingListItems, profile, diffingProfile),
    },
    {
      id: 'secondary',
      ...getIndividualSecondaryGroupItems(listItems, existingListItems, profile, diffingProfile),
    },
  ];

  if (profile?.investmentProfile || diffingProfile?.investmentProfile) {
    groups.push({
      id: IndividualProfileTableOfContentsItem.InvestmentProfile,
      title: 'Investment Profile',
      ...getInvestmentProfileGroupItems(listItems, existingListItems, profile, diffingProfile),
    });

    if (profile?.investmentProfile?.investmentExperience || diffingProfile?.investmentProfile?.investmentExperience) {
      groups.push({
        id: 'investmentExperience',
        title: 'Investment Experience',
        ...getInvestmentExperienceGroupItems(listItems, existingListItems, profile, diffingProfile),
      });
    }
  }

  if (profile?.w9 || diffingProfile?.w9) {
    groups.push({
      id: IndividualProfileTableOfContentsItem.W9,
      title: 'W-9',
      ...getIndividualW9GroupItems(listItems, existingListItems, profile, diffingProfile),
    });
  }

  let hasAssignedAffiliationsTocItem = false;

  for (
    let i = 0;
    i < Math.max(profile?.affiliations?.associatedPersons?.length || 0, diffingProfile?.affiliations?.associatedPersons?.length || 0);
    i += 1
  ) {
    groups.push({
      id: hasAssignedAffiliationsTocItem ? `affiliations.associatedPersons.${i}` : IndividualProfileTableOfContentsItem.Affiliations,
      ...getAssociatedPersonGroupItems(i, listItems, existingListItems, profile, diffingProfile),
    });

    hasAssignedAffiliationsTocItem = true;
  }

  if (profile?.affiliations?.politicallyExposedPerson || diffingProfile?.affiliations?.politicallyExposedPerson) {
    groups.push({
      id: hasAssignedAffiliationsTocItem ? 'affiliations.politicallyExposedPerson' : IndividualProfileTableOfContentsItem.Affiliations,
      title: 'Politically Exposed Person',
      ...getPoliticallyExposedPersonGroupItems(listItems, existingListItems, profile, diffingProfile),
    });

    hasAssignedAffiliationsTocItem = true;
  }

  if (profile?.affiliations?.corporateInsider || diffingProfile?.affiliations?.corporateInsider) {
    groups.push({
      id: hasAssignedAffiliationsTocItem ? 'affiliations.corporateInsider' : IndividualProfileTableOfContentsItem.Affiliations,
      title: 'Corporate Insider',
      ...getCorporateInsiderGroupItems(listItems, existingListItems, profile, diffingProfile),
    });

    hasAssignedAffiliationsTocItem = true;
  }

  for (let i = 0; i < Math.max(profile?.trustedPersons?.length || 0, diffingProfile?.trustedPersons?.length || 0); i += 1) {
    groups.push({
      id: i === 0 ? IndividualProfileTableOfContentsItem.TrustedContacts : `trustedContactPersons.${i}`,
      ...getTrustedContactPersonGroupItems(i, listItems, existingListItems, profile, diffingProfile),
    });
  }

  return groups;
}

function getOrganizationMainGroupItems(
  items: ReturnType<typeof getOrganizationProfileTypeListItems>,
  existingItems: ReturnType<typeof getOrganizationProfileTypeListItems>,
  newData: IxcRegistrationV1OrganizationProfile | undefined,
  existingData: IxcRegistrationV1OrganizationProfile | undefined,
) {
  const mainItemSet = existingData ? existingItems : items;

  return {
    items: [
      mainItemSet.mergedName,
      mainItemSet.taxIdentifier,
      mainItemSet.entityType,
      mainItemSet.businessClassification,
      mainItemSet.countryOfFormation,
      ...(mainItemSet.stateOfFormation ? [mainItemSet.stateOfFormation] : []),
      mainItemSet.dateOfFormation,
      mainItemSet.natureOfBusiness,
      mainItemSet.email,
      mainItemSet.phone,
      mainItemSet.companyAddress.formatted,
      mainItemSet.mailingAddress.formatted,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newData
        ? ([
            ...(isDeepEqual({ legalName: existingData.legalName, dba: existingData.dbaName }, { legalName: newData.legalName, dba: newData.dbaName })
              ? []
              : [items.mergedName]),
            ...(isDeepEqual(existingData.taxIdentifier, newData.taxIdentifier) ? [] : [items.taxIdentifier]),
            ...(isDeepEqual(existingData.entityType, newData.entityType) ? [] : [items.entityType]),
            ...(isDeepEqual(existingData.businessClassification, newData.businessClassification) ? [] : [items.businessClassification]),
            ...(isDeepEqual(existingData.countryOfFormation, newData.countryOfFormation) ? [] : [items.countryOfFormation]),
            ...(!items.stateOfFormation || isDeepEqual(existingData.stateOfFormation, newData.stateOfFormation) ? [] : [items.stateOfFormation]),
            ...(isDeepEqual(existingData.dateOfFormation, newData.dateOfFormation) ? [] : [items.dateOfFormation]),
            ...(isDeepEqual(existingData.natureOfBusiness, newData.natureOfBusiness) ? [] : [items.natureOfBusiness]),
            ...(isDeepEqual(existingData.email, newData.email) ? [] : [items.email]),
            ...(isDeepEqual(existingData.phone?.map(normalizePhone), newData.phone?.map(normalizePhone)) ? [] : [items.phone]),
            ...(isDeepEqual(existingData.companyAddress, newData.companyAddress) ? [] : [items.companyAddress.formatted]),
            ...(isDeepEqual(existingData.mailingAddress, newData.mailingAddress) ? [] : [items.mailingAddress.formatted]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

function getOrganizationW9GroupItems(
  items: ReturnType<typeof getOrganizationProfileTypeListItems>,
  existingItems: ReturnType<typeof getOrganizationProfileTypeListItems>,
  newData: IxcRegistrationV1OrganizationProfile | undefined,
  existingData: IxcRegistrationV1OrganizationProfile | undefined,
) {
  const mainItemSet = existingData ? existingItems : items;

  return {
    items: [
      mainItemSet.w9.isSubjectToBackupWithholding,
      mainItemSet.w9.certification,
      mainItemSet.w9.foreignOwners,
      mainItemSet.w9.exemptPayeeCode,
      mainItemSet.w9.signed,
    ] as const satisfies ReadonlyListItem[],
    changeItems:
      existingData && newData
        ? ([
            ...(isDeepEqual(Boolean(existingData.w9?.isSubjectToBackupWithholding), Boolean(newData.w9?.isSubjectToBackupWithholding))
              ? []
              : [items.w9.isSubjectToBackupWithholding]),
            ...(isDeepEqual(Boolean(existingData.w9?.certification), Boolean(newData.w9?.certification)) ? [] : [items.w9.certification]),
            ...(isDeepEqual(Boolean(existingData.w9?.foreignOwners), Boolean(newData.w9?.foreignOwners)) ? [] : [items.w9.foreignOwners]),
            ...(isDeepEqual(existingData.w9?.exemptPayeeCode, newData.w9?.exemptPayeeCode) ? [] : [items.w9.exemptPayeeCode]),
            ...(isDeepEqual(existingData.w9?.signed, newData.w9?.signed) ? [] : [items.w9.signed]),
          ] as const satisfies ReadonlyListItem[])
        : undefined,
  } as const;
}

export function getRegistrationOrganizationProfileListItemSummary(
  profile: IxcRegistrationV1OrganizationProfile | undefined,
  diffingProfile?: IxcRegistrationV1OrganizationProfile,
) {
  const listItems = getOrganizationProfileTypeListItems(profile);
  const diffingListItems = getOrganizationProfileTypeListItems(diffingProfile);

  return [
    {
      id: 'main',
      ...getOrganizationMainGroupItems(listItems, diffingListItems, profile, diffingProfile),
    },
    {
      id: OrganizationProfileTableOfContentsItem.W9,
      title: 'W-9',
      ...getOrganizationW9GroupItems(listItems, diffingListItems, profile, diffingProfile),
    },
  ] satisfies DiffedReadonlyListGroup[];
}

export function getRegistrationProfileStateListItems(
  data: IxcRegistrationV1ProfileState | undefined,
  linkContext: ReplaceValuesAndMakeOptional<IxcRegistrationV1ProfileKeys, string, boolean> = { profileId: false, programId: true },
) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1ProfileStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <ProfileStatusPill status={data.status} /> : null),
    profileId: getReadonlyListItemId(
      'profileId',
      'Profile ID',
      data?.profileId,
      linkContext.profileId ? getRegistrationProfileDetailPath(data?.profileId) : undefined,
    ),
    programId: getReadonlyListItemId(
      'programId',
      'Program ID',
      data?.programId,
      linkContext.programId ? getProgramDetailPath(data?.programId) : undefined,
    ),
    data: {
      currentVersion: data?.data?.currentVersion
        ? getRegistrationProfileVersionListItems(data.data.currentVersion, 'data.currentVersion.')
        : undefined,
      pendingVersion: data?.data?.pendingVersion
        ? getRegistrationProfileVersionListItems(data.data.pendingVersion, 'data.pendingVersion.')
        : undefined,
      accountingGroup: getReadonlyListItemTranslatedString('data.accountingGroup', 'Accounting Group', {
        namespace: 'ixcRegistrationV1',
        enumName: 'IxcRegistrationV1AccountingGroup',
        value: data?.data?.accountingGroup,
      }),
    },
  } as const satisfies ReadonlyListItemRecord;
}
